import { Directive, ViewChild, OnDestroy } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Subscription } from 'rxjs'
import { Table } from 'primeng/table'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { ApiService } from 'src/app/services/api.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService, RoleItem } from 'src/app/services/role-loading.service'

@Directive()
export abstract class ApiKeyModalBaseComponent implements OnDestroy {
  @ViewChild('rolesTable') rolesTable: Table

  apiKeyForm: FormGroup
  availableRoles: RoleItem[] = []
  selectedRoles: RoleItem[] = []
  loadingRoles = false
  saving = false
  saveSubscription: Subscription = null

  abstract modal: ModalDialogComponent

  constructor(
    protected formBuilder: FormBuilder,
    protected accountService: AccountService,
    protected uiService: UiService,
    protected apiService: ApiService,
    public accessLevelService: AccessLevelService,
    protected roleLoadingService: RoleLoadingService
  ) {
    this.initializeForm()
  }

  ngOnDestroy() {
    this.cleanup()
  }

  protected initializeForm() {
    this.apiKeyForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      enabled: [true],
    })
  }

  close() {
    this.modal.close()
    this.resetForm()
  }

  isModalOpen(): boolean {
    return this.modal && this.modal.isOpen
  }

  protected resetForm() {
    this.apiKeyForm.reset({ enabled: true })
    this.selectedRoles = []
    this.saving = false
    this.cleanup()
  }

  protected async loadAvailableRoles(): Promise<void> {
    this.loadingRoles = true
    try {
      this.availableRoles = await this.roleLoadingService.loadRoles()
    } catch (error) {
      console.error('Failed to load roles:', error)
      this.availableRoles = []
    } finally {
      this.loadingRoles = false
    }
  }

  // Role selection methods
  isRoleSelected(role: RoleItem): boolean {
    return this.selectedRoles.some((r) => r.id === role.id)
  }

  toggleRole(role: RoleItem, event: any) {
    if (event.target.checked) {
      if (!this.isRoleSelected(role)) {
        this.selectedRoles.push(role)
      }
    } else {
      this.selectedRoles = this.selectedRoles.filter((r) => r.id !== role.id)
    }
  }

  toggleAllRoles(event: any) {
    if (event.target.checked) {
      this.selectedRoles = [...this.availableRoles]
    } else {
      this.selectedRoles = []
    }
  }

  areAllRolesSelected(): boolean {
    return this.availableRoles.length > 0 && this.selectedRoles.length === this.availableRoles.length
  }

  areSomeRolesSelected(): boolean {
    return this.selectedRoles.length > 0 && this.selectedRoles.length < this.availableRoles.length
  }

  filterRoles(event: any) {
    if (this.rolesTable) {
      this.rolesTable.filterGlobal(event.target.value, 'contains')
    }
  }

  // Form validation
  get isFormValid(): boolean {
    return this.apiKeyForm.valid
  }

  get nameErrors(): string | null {
    const control = this.apiKeyForm.get('name')
    if (control?.errors && control?.touched) {
      if (control.errors['required']) {
        return 'API Key name is required.'
      }
    }
    return null
  }

  // Helper methods
  isDefaultAccountRole(role: RoleItem): boolean {
    return this.accessLevelService.isDefaultAccountRole(role)
  }

  getAccessLevelTooltip(accessLevelInfo: any): string {
    if (!accessLevelInfo.applications || accessLevelInfo.applications.length === 0) {
      return 'All Applications'
    }
    return accessLevelInfo.applications.map((name: string) => `<span class="tooltip-badge">${name}</span>`).join('')
  }

  customSort = this.accessLevelService.createAccessLevelSortFunction()

  protected cleanup() {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe()
      this.saveSubscription = null
    }
  }

  abstract getSaveButtonText(): string
}
